import * as yup from 'yup';
import registrationFormModel from './registrationFormModel';
import { MESSAGE_NOT_A_VALUE } from '../../utils/constant';
import { countryCodes, pcsFilter } from '../../utils/util';
import { CountryCode } from 'libphonenumber-js/types';

const cardNumberRegEx = /^(?:4[0-9]{12}(?:[0-9]{3})?)$/;

const {
  registrationDetailsFormField: {
    IMCNumber,
    RegistrationType,
    Scheme,
    EngagedInPracticeOfMedicine,
    UserConfirmation,
    Specialty,
    SubSpecialty
  },
  personalDetailsFormField: {
    Title,
    FirstName,
    LastName,
    DOB,
    Gender,
    Nationality,
    MobileNumber
  },
  educationalDetailsFormField: {
    QualificationCountry,
    PrimaryMedicalDegreeCollege,
    OtherPrimaryMedicalDegreeCollege,
    QualificationYear
  },
  workDetailsFormField: {
    StartDate,
    PostLevel,
    Country,
    NameOfEmployer,
    OtherNameOfEmployer,
    NameOfClinicalSupervisor,
    JobTitle,
    CurrentUserRoles
  },
  paymentDetailsFormField: {
    NameOnCard,
    CardNumber,
    ExpiryDate,
    CVV
  }
} = registrationFormModel;

const postLevelValidationSchema = yup.string().required(`${PostLevel.requiredErrorMsg}`)
  .when([CurrentUserRoles.name],
    ([currentUserRoles]) => {
      const userRoles = currentUserRoles as unknown as string[];
      if (pcsFilter(userRoles)) {
        return yup.string().required(`${PostLevel.requiredErrorMsg}`);
      }
      return yup.string().notRequired();
    });

export const WORK_START_DATE_MIN = 'MinimumWorkStartDate';

export const WorkDetailsValidationSchema = yup.object().shape({
  [StartDate.name]: yup.date().nullable()
    .typeError(StartDate.typeError)
    .required(`${StartDate.requiredErrorMsg}`)
    .when(WORK_START_DATE_MIN, ([minimumStartDate], schema) => minimumStartDate
      ? schema.min(minimumStartDate, StartDate.minimumStartDateErrorMsg)
      : schema)
    .test('not-in-future', StartDate.maximumStartDateErrorMsg, value => {
      if (!value) return true;
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return value <= today;
    }),
  [PostLevel.name]: postLevelValidationSchema,
  [Country.name]: yup.string().required(`${Country.requiredErrorMsg}`),
  [NameOfEmployer.name]: yup.string().when([OtherNameOfEmployer.name], ([otherNameOfEmployer], schema) => {
    return schema.test(`${NameOfEmployer.name}`, `${NameOfEmployer.requiredErrorMsg}`, function (nameOfEmployer) {
      if (nameOfEmployer === MESSAGE_NOT_A_VALUE) {
        return true;
      }
      if (otherNameOfEmployer !== "" || nameOfEmployer !== "") {
        return true;
      }
      return false;
    })
  }),
  [OtherNameOfEmployer.name]: yup.string().max(OtherNameOfEmployer.maxStringCount, OtherNameOfEmployer.maxStringLengthErrorMsg),
  [JobTitle.name]: yup.string().required(`${JobTitle.requiredErrorMsg}`).max(JobTitle.maxStringCount, JobTitle.maxStringLengthErrorMsg),
  [NameOfClinicalSupervisor.name]: yup.string().max(NameOfClinicalSupervisor.maxStringCount, NameOfClinicalSupervisor.maxStringLengthErrorMsg)
});


export const EducationalDetailsValidationSchema = yup.object().shape({
  [QualificationCountry.name]: yup.string().required(`${QualificationCountry.requiredErrorMsg}`),
  [PrimaryMedicalDegreeCollege.name]: yup.string().when([OtherPrimaryMedicalDegreeCollege.name], ([otherPrimaryMedicalDegreeCollege], schema) => {
    return schema.test(`${PrimaryMedicalDegreeCollege.name}`, `${PrimaryMedicalDegreeCollege.requiredErrorMsg}`, function (primaryMedicalDegreeCollege) {
      if (primaryMedicalDegreeCollege === MESSAGE_NOT_A_VALUE) {
        return true;
      }
      if (otherPrimaryMedicalDegreeCollege !== "" || primaryMedicalDegreeCollege !== "") {
        return true;
      }
      return false;
    });
  }),
  [OtherPrimaryMedicalDegreeCollege.name]: yup.string().max(OtherPrimaryMedicalDegreeCollege.maxStringCount, OtherPrimaryMedicalDegreeCollege.maxStringLengthErrorMsg),
  [QualificationYear.name]: yup.string().required(`${QualificationYear.requiredErrorMsg}`)
});

export const imcNumberValidationSchema = yup.string()
  .required(IMCNumber.requiredErrorMsg)
  .matches(/^[0-9]+$/, IMCNumber.typeError)
  .min(IMCNumber.minStringCount, IMCNumber.minErrorMessage)
  .max(IMCNumber.maxStringCount, IMCNumber.maxErrorMessage);

export const registrationValidationSchema: yup.AnyObjectSchema[] = [
  yup.object().shape({
    [IMCNumber.name]: imcNumberValidationSchema,
    [RegistrationType.name]: yup.string().required(`${RegistrationType.requiredErrorMsg}`),
    [Scheme.name]: yup.string().required(`${Scheme.requiredErrorMsg}`),
    [EngagedInPracticeOfMedicine.name]: yup.string().required(`${EngagedInPracticeOfMedicine.requiredErrorMsg}`),
    [Specialty.name]: yup.string().required(`${Specialty.requiredErrorMsg}`),
    [SubSpecialty.name]: yup.string().max(SubSpecialty.maxStringCount, SubSpecialty.maxStringLengthErrorMsg),
    [UserConfirmation.name]: yup.array().min(1, UserConfirmation.requiredErrorMsg).of(yup.string().required(UserConfirmation.requiredErrorMsg))
  }),
  yup.object({
    [Title.name]: yup.string().required(`${Title.requiredErrorMsg}`),
    [FirstName.name]: yup.string().required(`${FirstName.requiredErrorMsg}`)
      .max(FirstName.maxStringCount, FirstName.maxStringLengthErrorMsg),
    [LastName.name]: yup.string().required(`${LastName.requiredErrorMsg}`)
      .max(LastName.maxStringCount, LastName.maxStringLengthErrorMsg),
    [DOB.name]: yup.string().nullable().required(`${DOB.requiredErrorMsg}`),
    [Gender.name]: yup.string().required(`${Gender.requiredErrorMsg}`),
    [Nationality.name]: yup.string().required(`${Nationality.requiredErrorMsg}`),
    [MobileNumber.name]: yup.string().phone(countryCodes as CountryCode[], `${MobileNumber.typeError}`)
      .required(`${MobileNumber.requiredErrorMsg}`)
      .max(69, MobileNumber.maxStringLengthErrorMsg)
  }),
  EducationalDetailsValidationSchema,
  WorkDetailsValidationSchema,
  yup.object({
    [NameOnCard.name]: yup.string().required(`${NameOnCard.requiredErrorMsg}`),
    [CardNumber.name]: yup.string()
      .required(`${CardNumber.requiredErrorMsg}`)
      .matches(cardNumberRegEx, CardNumber.invalidErrorMsg),
    [ExpiryDate.name]: yup.string().required(`${ExpiryDate.requiredErrorMsg}`),
    [CVV.name]: yup.string()
      .required(`${CVV.requiredErrorMsg}`)
      .test('len', `${CVV.invalidErrorMsg}`, (val: any) => val && val.length === 3)
  })
]
